/**
 * League detail: a branded competition hero (floodlit-stadium backdrop, crest,
 * season chip, favorite) with a glassmorphic straddling tab bar, then per-tab
 * bodies (Table / Knockout / Fixtures / Player stats / Team stats).
 *
 * Table + Player stats + Fixtures + Team stats are wired to live data via
 * `src/leagueDetail` hooks. The Table tab hides for leagues with no standings
 * snapshot; the Knockout tab only shows when the schedule has knockout fixtures.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text, ScrollView, Pressable, useWindowDimensions } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ChevronDown, ChevronLeft, Star } from 'lucide-react-native'
import { useRouter } from 'expo-router'
import { useTheme } from '../../../src/theme/useTheme'
import { useFavoriteLeagues } from '../../../src/create/hooks/useFavoriteLeagues'
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag'
import { MatchHubHeroBackground } from '../../match/MatchHubHero/MatchHubHeroBackground'
import { getLeagueDetailViewStyles } from './LeagueDetailView.styles'
import { AnimatedSegmentedControl } from '../../ui/AnimatedSegmentedControl'
import { CompetitionTabBar } from '../CompetitionTabBar/CompetitionTabBar'
import {
  FALLBACK_SEASON_LABEL,
  LEAGUE_DETAIL_TAB_LABELS,
  LEAGUE_DETAIL_TABLE_MODE_OPTIONS,
  LEAGUE_DETAIL_HIT_SLOP,
  type LeagueDetailNavTabKey,
} from '../leagueDetail.constants'
import type { LeagueStandingsScope } from '../../../src/leagueDetail/types/leagueDetail.types'
import {
  useLeagueAnalytics,
  useLeagueSchedule,
  useLeagueStandings,
} from '../../../src/leagueDetail'
import { LeaguePlayerBoards } from './LeaguePlayerBoards'
import { LeagueFixturesList } from './LeagueFixturesList'
import { LeagueTeamStats } from './LeagueTeamStats'
import {
  LeagueStandingsTable,
  type LeagueStandingsTableMode,
} from './LeagueStandingsTable'
import { LeagueStandingsScopePicker } from './LeagueStandingsScopePicker'
import { LeagueKnockoutTab } from './LeagueKnockoutTab'
import {
  hasLeagueKnockout,
  isNationalGroupTournament,
} from '../../../src/leagueDetail/helpers/buildLeagueKnockoutBracket.helper'
import { formatStandingsGroupName } from '../../../src/leagueDetail/helpers/mapLeagueStandings.helper'

export interface LeagueDetailViewProps {
  leagueName: string
  leagueId: string
  /** Normalized flagcdn code (e.g. `gb-eng`). Omitted when unknown — no placeholder badge. */
  countryCode?: string
}

export const LeagueDetailView: React.FC<LeagueDetailViewProps> = ({
  leagueName,
  leagueId,
  countryCode,
}) => {
  const theme = useTheme()
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const { width: windowWidth } = useWindowDimensions()
  const styles = useMemo(
    () => getLeagueDetailViewStyles(theme, windowWidth, insets.top),
    [theme, windowWidth, insets.top],
  )
  const { favoriteLeagueIds, toggleFavorite } = useFavoriteLeagues()

  const [tableScope, setTableScope] = useState<LeagueStandingsScope>('all')
  const standings = useLeagueStandings(leagueId, { scope: tableScope })
  const analytics = useLeagueAnalytics(leagueId)
  const schedule = useLeagueSchedule(leagueId)

  const hasTableData = standings.data != null
  const shouldHideTable = standings.isEmpty && !standings.isLoading
  const hasKnockout = useMemo(
    () => hasLeagueKnockout(schedule.matches),
    [schedule.matches],
  )
  // National-team group tournaments (e.g. World Cup) reached via the league page have no knockout
  // fixtures until the draw is synced — show a provisional bracket instead of hiding the tab.
  const showProvisionalKnockout = useMemo(
    () =>
      !hasKnockout &&
      isNationalGroupTournament(
        schedule.matches,
        (standings.data?.groups ?? []).map((g) => g.name),
      ),
    [hasKnockout, schedule.matches, standings.data],
  )
  const knockoutVisible = hasKnockout || showProvisionalKnockout

  const visibleTabs = useMemo<LeagueDetailNavTabKey[]>(() => {
    const tabs: LeagueDetailNavTabKey[] = []
    if (!shouldHideTable) tabs.push('table')
    if (knockoutVisible) tabs.push('knockout')
    tabs.push('fixtures', 'playerStats', 'teamStats')
    return tabs
  }, [shouldHideTable, knockoutVisible])

  const tabBarTabs = useMemo(
    () => visibleTabs.map((key) => ({ key, label: LEAGUE_DETAIL_TAB_LABELS[key] })),
    [visibleTabs],
  )

  const [activeTab, setActiveTab] = useState<LeagueDetailNavTabKey>(() => visibleTabs[0] ?? 'fixtures')
  const [tableMode, setTableMode] = useState<LeagueStandingsTableMode>('full')

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] ?? 'fixtures')
    }
  }, [visibleTabs, activeTab])

  useEffect(() => {
    if (!standings.hasHomeAwaySplit && tableScope !== 'all') {
      setTableScope('all')
    }
  }, [standings.hasHomeAwaySplit, tableScope])

  const isFavorite = favoriteLeagueIds.has(leagueId)

  const handleFavoritePress = useCallback(() => {
    void toggleFavorite(leagueId)
  }, [leagueId, toggleFavorite])

  const handleBackPress = useCallback(() => {
    if (router.canGoBack()) {
      router.back()
      return
    }
    router.replace('/app/gameResults')
  }, [router])

  const seasonLabel = standings.data?.league.season ?? FALLBACK_SEASON_LABEL
  const countryLabel = standings.data?.league.country ?? null
  const standingsGroups = standings.data?.groups ?? []
  const hasMultipleGroups = standingsGroups.length > 1

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
      nestedScrollEnabled
    >
      <View style={styles.heroShell}>
        <View style={styles.heroBlock}>
          {/* Floodlit-stadium backdrop reused from the fixture page. Rendered as a direct
           *  child of the flow-sized hero block (not inside an extra absolute wrapper) so the
           *  background's percentage-height pitch/SVG layers resolve — matching MatchHubHero. */}
          <MatchHubHeroBackground />
          <LinearGradient
            colors={['rgba(15,10,26,0.35)', 'rgba(15,10,26,0.78)']}
            locations={[0.35, 1]}
            style={styles.heroScrim}
            pointerEvents="none"
          />

          <View style={styles.heroContent}>
          <View style={styles.topBar}>
            <Pressable
              onPress={handleBackPress}
              hitSlop={LEAGUE_DETAIL_HIT_SLOP}
              style={({ pressed }) => [styles.backButton, pressed && styles.backButtonPressed]}
              accessibilityRole="button"
              accessibilityLabel="Go back"
            >
              <ChevronLeft size={22} color={theme.white} />
            </Pressable>
          </View>

          <View style={styles.heroTopRow}>
            {countryCode != null && countryCode !== '' ? (
              <View style={styles.leagueHeroFlag}>
                <CountryFlag
                  countryCode={countryCode}
                  size="default"
                  accessibilityLabel={`${leagueName} country flag`}
                />
              </View>
            ) : null}
            <View style={styles.heroTitles}>
              <Text style={styles.leagueName} numberOfLines={2}>{leagueName}</Text>
              {countryLabel != null && countryLabel !== '' ? (
                <Text style={styles.leagueSubtitle} numberOfLines={1}>{countryLabel}</Text>
              ) : null}
            </View>
            <Pressable
              onPress={handleFavoritePress}
              hitSlop={LEAGUE_DETAIL_HIT_SLOP}
              style={({ pressed }) => [
                styles.favoriteFab,
                isFavorite && styles.favoriteFabActive,
                pressed && styles.favoriteFabPressed,
              ]}
              accessibilityRole="button"
              accessibilityLabel={
                isFavorite ? `Unfavorite ${leagueName}` : `Favorite ${leagueName}`
              }
              accessibilityState={{ checked: isFavorite }}
            >
              <Star
                size={22}
                color={isFavorite ? theme.accent : theme.white}
                fill={isFavorite ? theme.accent : 'transparent'}
              />
            </Pressable>
          </View>

          <Pressable style={styles.seasonPill} accessibilityRole="button">
            <Text style={styles.seasonPillText}>{seasonLabel}</Text>
            <ChevronDown size={16} color={theme.gray[500]} />
          </Pressable>
          </View>
        </View>

        <View style={styles.tabStrip}>
          <CompetitionTabBar<LeagueDetailNavTabKey>
            tabs={tabBarTabs}
            activeKey={activeTab}
            onChange={setActiveTab}
            accent={theme.primary}
            testID="league-detail-tab-bar"
          />
        </View>
      </View>

      <View style={styles.mainSurface}>
        {activeTab === 'table' && hasTableData && (
          <>
            <View style={styles.filterRow}>
              <AnimatedSegmentedControl<LeagueStandingsTableMode>
                variant="sportsBar"
                options={LEAGUE_DETAIL_TABLE_MODE_OPTIONS}
                value={tableMode}
                onChange={setTableMode}
                testID="league-detail-table-mode"
              />
              <LeagueStandingsScopePicker
                value={tableScope}
                onChange={setTableScope}
                venueSplitAvailable={standings.hasHomeAwaySplit}
              />
            </View>

            {standingsGroups.map((group) => (
              <View key={group.name} style={styles.standingsGroupBlock}>
                {hasMultipleGroups ? (
                  <Text style={styles.standingsGroupHeading}>
                    {formatStandingsGroupName(group.name)}
                  </Text>
                ) : null}
                <LeagueStandingsTable rows={group.rows} mode={tableMode} />
              </View>
            ))}
          </>
        )}

        {activeTab === 'knockout' && (
          <LeagueKnockoutTab matches={schedule.matches} provisional={showProvisionalKnockout} />
        )}

        {activeTab === 'fixtures' && (
          <LeagueFixturesList
            matches={schedule.matches}
            isLoading={schedule.isLoading}
            hasNextPage={schedule.hasNextPage}
            isFetchingNextPage={schedule.isFetchingNextPage}
            onLoadMore={schedule.fetchNextPage}
          />
        )}

        {activeTab === 'playerStats' && (
          <LeaguePlayerBoards boards={analytics.boards} isLoading={analytics.isLoading} />
        )}

        {activeTab === 'teamStats' && (
          hasTableData ? (
            <LeagueTeamStats groups={standingsGroups} accent={theme.primary} />
          ) : (
            <View style={styles.placeholderCard}>
              <Text style={styles.placeholderTitle}>{LEAGUE_DETAIL_TAB_LABELS.teamStats}</Text>
              <Text style={styles.placeholderBody}>
                Team analytics appear once this league has a standings snapshot.
              </Text>
            </View>
          )
        )}
      </View>
    </ScrollView>
  )
}
